import { FC, SVGProps } from "react";
import { I18n } from "../hooks/useTranslations";

export type Glyph = FC<SVGProps<SVGSVGElement>>;

const languageCodes = <const>["en-US", "en-gb", "it-IT", "de-DE", "fr-FR", "es-ES"];
export type LanguageCode = (typeof languageCodes)[number];

export const isLanguageCode = (value: string): value is LanguageCode => languageCodes.includes(value as LanguageCode);

export type FetcherOptions = {
  url: string;
  signal?: AbortSignal;
  headers?: Headers;
};

export type FilterOperator = "gte" | "lte" | "between";

export interface FilterParam {
  fieldname: string;
  operator?: FilterOperator;
  values?: Exclude<MenuOptionValue, Lookup>[];
}

// Value shape as stored/reported back by useListFilters (Lookups already unwrapped to their .id
// string before being stored, same as today) - what ColumnFilterProps.selected receives.
export type StoredColumnFilterValue = {
  operator?: FilterOperator;
  values: Exclude<MenuOptionValue, Lookup>[];
};

// Value shape a filter component sends back via onChange. Checkbox-list based filters toggle raw
// values straight from the server, which may still be Lookup objects (e.g. a subsidiary id column)
// - unwrapping to .id happens inside useListFilters.setFilters. Only gt/lt/between-producing modes
// ever emit non-Lookup scalars.
export type ColumnFilterValue = {
  operator?: FilterOperator;
  values: MenuOptionValue[];
};

export type ListFilters = {
  page: number;
  limit: number;
  q?: string;
  orderBy?: string;
  desc: boolean;
  filters?: FilterParam[];
};

export type Paginated<P> = {
  items: P[];
  total: number;
};

export type PaginationQuery = Partial<ListFilters>;
export type PaginationQueryWithFields<T, F extends DotNestedKeys<T> = DotNestedKeys<T>> = PaginationQuery & {
  fields: ReadonlyArray<F>;
};
/* --- utility types to produce dot-path keys --- */

// Helper: treat plain object-like types (not arrays/functions) as "expandable"
// type IsPlainObject<T> = T extends Function ? false : T extends any[] ? false : T extends object ? true : false;

// // Recursive builder: for each string key K of T, include K and, if the value is a plain object,
// // include `K.${nested}` for every nested key.
// type DotNestedKeys<T> = T extends object
//   ? {
//       [K in Extract<keyof T, string>]: IsPlainObject<T[K]> extends true
//         ? K | `${K}.${DotNestedKeys<T[K]>}` // include nested dot-paths
//         : K; // leaf key
//     }[Extract<keyof T, string>]
//   : never;

export type Lookup = {
  id: string;
  value: string;
};

export const isLookup = (value: MenuOptionFilterValue): value is Lookup => {
  return !!value && !!(value as Lookup).id && !!(value as Lookup).value;
};

export type MenuOptionFilterValue = Lookup | string | number | boolean | null;
export type MenuOptionValue = MenuOptionFilterValue | MenuOptionFilterValue[];

// // utils
// type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void ? I : never;

// type Simplify<T> = { [K in keyof T]: T[K] };

// // Recursive builder: turn each path string into a (nested) object type, then intersect them.
// type NestedPickImpl<T, P extends string> = P extends `${infer Key}.${infer Rest}`
//   ? Key extends keyof T
//     ? T[Key] extends object | undefined | null
//       ? { [K in Key]: NestedPickImpl<NonNullable<T[Key]>, Rest> }
//       : never
//     : never
//   : P extends keyof T
//   ? { [K in P]: T[P] }
//   : never;

// // The public type: take union of path strings P, map each to an object,
// // convert union -> intersection to merge siblings, then simplify the result.
// export type NestedPick<T, P extends string> = Simplify<
//   UnionToIntersection<P extends any ? NestedPickImpl<T, P> : never>
// >;

export type AuthorEditor = {
  id: string;
  email: string;
  fullName: string;
};

export type Entity = {
  id: string;
  author: AuthorEditor;
  createdAt: string;
  editor: AuthorEditor;
  modifiedAt: string;
  versionNumber: number;
};

export type Version = {
  author: AuthorEditor;
  createdAt: string;
  versionNumber: number;
};

export type Versions<T> = {
  current: T;
  next: T;
};

export interface FileInfo {
  data: string;
  contentType: string;
  name?: string;
}

export type Currency = {
  value: CurrencyString;
  label: string;
  symbol: string;
};

export type CurrencyString = "EUR" | "USD" | "JPY" | "GBP" | "CNY";

export const getCurrencies = (): Currency[] => [
  { value: "EUR", label: "€ - EUR", symbol: "€" },
  { value: "USD", label: "$ - USD", symbol: "$" },
  { value: "JPY", label: "¥ - JPY", symbol: "¥" },
  { value: "GBP", label: "£ - GBP", symbol: "£" },
  { value: "CNY", label: "¥ - CNY", symbol: "¥" },
];

export type ForeignPrice = {
  Value: number;
  Currency: CurrencyString;
};

export type Price = {
  EuroValue: number;
  ForeignValue?: ForeignPrice;
};

export type MeasurmentType = {
  value: "metric" | "imperial";
  label: string;
};

export const getMeasurmentTypes = (i18n: I18n): MeasurmentType[] => [
  { value: "metric", label: i18n.t.common.metric },
  { value: "imperial", label: i18n.t.common.imperial },
];

export type Language = {
  value: LanguageCode;
  label: string;
};

export const getLanguages = (i18n: I18n): Language[] => [
  { label: i18n.t.common.englishUk, value: "en-gb" },
  { label: i18n.t.common.englishUs, value: "en-US" },
  { label: i18n.t.common.german, value: "de-DE" },
  { label: i18n.t.common.french, value: "fr-FR" },
  { label: i18n.t.common.italian, value: "it-IT" },
  { label: i18n.t.common.spanish, value: "es-ES" },
];
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void ? I : never;
type Simplify<T> = { [K in keyof T]: T[K] } & {};
type IsPlainObject<T> = T extends Function ? false : T extends any[] ? false : T extends object ? true : false;
type IsOptionalProp<T, K extends keyof T> = {} extends Pick<T, K> ? true : false;

export type DotNestedKeys<T> = T extends object
  ? {
      [K in Extract<keyof T, string>]: NonNullable<T[K]> extends Array<infer U>
        ? K | `${K}[]` | `${K}[].${DotNestedKeys<U>}`
        : IsPlainObject<NonNullable<T[K]>> extends true
          ? K | `${K}.${DotNestedKeys<NonNullable<T[K]>>}`
          : K;
    }[Extract<keyof T, string>]
  : never;

type _NestedPickSingle<T, P extends string> = P extends `${infer Key}[].${infer Rest}`
  ? Key extends keyof T
    ? NonNullable<T[Key]> extends Array<infer U>
      ? Extract<Rest, DotNestedKeys<U>> extends never
        ? never
        : IsOptionalProp<T, Key> extends true
          ? { [K in Key]?: Array<NestedPick<U, Extract<Rest, DotNestedKeys<U>>>> }
          : { [K in Key]: Array<NestedPick<U, Extract<Rest, DotNestedKeys<U>>>> }
      : never
    : never
  : P extends `${infer Key}[]`
    ? Key extends keyof T
      ? NonNullable<T[Key]> extends Array<infer U>
        ? IsOptionalProp<T, Key> extends true
          ? { [K in Key]?: Array<U> }
          : { [K in Key]: Array<U> }
        : never
      : never
    : P extends `${infer Key}.${infer Rest}`
      ? Key extends keyof T
        ? IsPlainObject<NonNullable<T[Key]>> extends true
          ? Extract<Rest, DotNestedKeys<NonNullable<T[Key]>>> extends never
            ? never
            : IsOptionalProp<T, Key> extends true
              ? { [K in Key]?: NestedPick<NonNullable<T[Key]>, Extract<Rest, DotNestedKeys<NonNullable<T[Key]>>>> }
              : { [K in Key]: NestedPick<NonNullable<T[Key]>, Extract<Rest, DotNestedKeys<NonNullable<T[Key]>>>> }
          : never
        : never
      : P extends keyof T
        ? IsOptionalProp<T, P> extends true
          ? { [K in P]?: T[P] }
          : { [K in P]: T[P] }
        : never;

export type NestedPick<T, P extends DotNestedKeys<T>> = Simplify<
  UnionToIntersection<P extends any ? _NestedPickSingle<T, P> : never>
>;
